import { cx } from '@emotion/css';
import type { RotateProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ODSIcons, { ODSIconNames } from '../../icons/ODSIcons';
import type { ODSIconName } from '../../icons/ODSIcons';
import type { FaSupportedIconNames } from '../../theme/registerIcons';
import { FA_SUPPORTED_ICONS } from '../../theme/registerIcons';
import type { FC, AriaAttributes } from 'react';
import type { Property } from 'csstype';

type IconName = ODSIconName | FaSupportedIconNames;
const faIconNames = Object.keys(FA_SUPPORTED_ICONS) as FaSupportedIconNames[];
const allIconNames = [...ODSIconNames, ...faIconNames];

type IconProps = Pick<
  React.ComponentProps<typeof FontAwesomeIcon>,
  'size' | 'className' | 'fixedWidth' | 'spin' | 'flip' | 'rotate' | 'color' | 'style' | 'listItem'
> &
  Pick<AriaAttributes, 'aria-label' | 'aria-labelledby'> & {
    /** Icon name either a FontAwesome icon or one of our ODSIcons */
    name: IconName;
    /** Set a custom width *for ODSIcons only* */
    width?: Property.Width;
    /** Set a custom height *for ODSIcons only* */
    height?: Property.Height;
    /** Custom data-testid attribute value */
    testId?: string;
  };

const isOdsIcon = (name: string): boolean => name in ODSIcons;

const IconFa: FC<IconProps> = (props) => {
  const icon = FA_SUPPORTED_ICONS[props.name];
  if (!icon) console.warn(`Icon "${props.name}" is not supported`);

  const hasLabel = Boolean(props['aria-label'] || props['aria-labelledby']);

  return (
    <FontAwesomeIcon
      icon={icon}
      size={props.size}
      fixedWidth={props.fixedWidth}
      className={props.className}
      spin={props.spin}
      flip={props.flip}
      rotation={props.rotate as RotateProp}
      color={props.color}
      style={props.style}
      listItem={props.listItem}
      data-testid={props.testId ?? `icon-${props.name}`}
      role={hasLabel ? 'img' : undefined}
      aria-hidden={hasLabel ? undefined : 'true'}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
    />
  );
};

const IconOds: FC<IconProps> = ({
  name,
  size,
  width = '1.25em',
  height = width,
  className,
  fixedWidth,
  spin,
  flip,
  rotate,
  color,
  style,
  listItem,
  testId,
  ...componentProps
}) => {
  const hasLabel = Boolean(componentProps['aria-label'] || componentProps['aria-labelledby']);
  return (
    <svg
      {...componentProps}
      data-testid={testId ?? `icon-${name}`}
      role={hasLabel ? 'img' : undefined}
      aria-hidden={!hasLabel ? 'true' : undefined}
      className={cx('ods-icon', className, size && `fa-${size}`, {
        'fa-spin': spin,
        'fa-flip-vertical': flip === 'vertical',
        'fa-flip-horizontal': flip === 'horizontal',
        'fa-flip-both': flip === 'both',
        'fa-rotate-90': rotate === '90' || rotate === 90,
        'fa-rotate-180': rotate === '180' || rotate === 180,
        'fa-rotate-270': rotate === '270' || rotate === 270,
        'fa-li ': listItem,
      })}
      style={{
        ...style,
        ...(color && { color }),
      }}
      focusable="false"
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={ODSIcons[name].viewBox}
    >
      {ODSIcons[name].paths}
    </svg>
  );
};

const Icon: FC<IconProps> = (props) => {
  return isOdsIcon(props.name) ? <IconOds {...props} /> : <IconFa {...props} />;
};

const isValidIconName = (name: string): boolean => allIconNames.includes(name as IconName);
const castIconName = (name: string): IconName =>
  (isValidIconName(name) ? name : 'circle-question') as IconName;

export default Icon;
export type { IconProps, IconName };
export { castIconName };
